import React, { useEffect, useState } from "react";
import HtmlReportWidgets from "./HtmlReportWidgets/";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { getBoardByIdAsync } from "../../redux/boardSlice";
import LoadingAnimation from "../../components/common/LoadingAnimation";
import AppVersionFooter from "../../components/common/AppVersionFooter";
import "./ReportHtmlView.css";

// how long to wait for the widgets before giving up and showing what we have
const LOAD_TIMEOUT_MS = 20000;

/* These come off the query string, so they are whatever is in the address bar. An
   unparseable value threw during render and this route has nothing above it to catch
   that - a hand-edited or truncated link showed a blank page. */
const parseQueryJson = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    console.error("Ignoring malformed report parameter:", raw, e);
    return null;
  }
};

export default function ReportHtmlView() {
  const dispatch = useDispatch();
  const { boardId } = useParams();
  const search = useLocation().search;
  const searchParams = new URLSearchParams(search);
  const boards = useSelector((state) => state.boards);

  /* Loading here is two separate waits - the board definition, then every widget's rows -
     and they used to look different from each other, so the page flicked between two
     spinners and a blank gap. This is one status for the whole thing.
     'loading' -> 'ready', or 'timeout' if the widgets never arrive. */
  const [status, setStatus] = useState("loading");

  const board = boards.selectedBoard;
  // selectedBoard is global, so it can still hold the previous report while this one
  // loads. Only trust it once it is the board this URL asked for.
  // (a board with no id is still rendered - better a possible flash than a page that
  // never shows anything if the API stops returning that field)
  const isCurrentBoard = Boolean(board && (!board.id || board.id === boardId));

  useEffect(() => {
    setStatus("loading");
    dispatch(getBoardByIdAsync({ id: boardId }));
  }, [boardId, dispatch]);

  useEffect(() => {
    if (status !== "loading") return undefined;
    const timer = setTimeout(
      () => setStatus((current) => (current === "loading" ? "timeout" : current)),
      LOAD_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [status, boardId]);

  const isLoading = status === "loading";

  return (
    <div className="rhv-page">
      <div className="rhv-header">
        <h2 className="rhv-title">{isCurrentBoard ? board.name : "Report"}</h2>
        {isLoading ? <p className="rhv-subtitle">Preparing your report...</p> : null}
      </div>

      {status === "timeout" ? (
        <div className="rhv-notice">
          <span>Some data took too long to load. Anything that did load is shown below.</span>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      ) : null}

      <div className="rhv-body">
        {isLoading ? (
          <div className="rhv-overlay">
            <div className="rhv-skeleton">
              <div className="rhv-skeleton-line rhv-skeleton-line--head" />
              <div className="rhv-skeleton-line" />
              <div className="rhv-skeleton-line" />
              <div className="rhv-skeleton-line" />
              <div className="rhv-skeleton-line" />
            </div>
            <LoadingAnimation size="large" text="Loading report…" />
          </div>
        ) : null}

        {isCurrentBoard ? (
          <HtmlReportWidgets
            /* remounts when the board changes - the widget grid does not refetch on a
               prop change, so without this a second drill-down would show the first
               report's data */
            key={boardId}
            board={board}
            selectedResource={null}
            bodyParams={parseQueryJson(searchParams.get("bodyParams"))}
            drillDownParams={parseQueryJson(searchParams.get("drillDownParams"))}
            onReportReady={() => setStatus("ready")}
          />
        ) : null}
      </div>

      {/* This page has a layout of its own rather than the signed-in shell's, so it
          mounts the version line itself. Only the PAGE does - a drill-down opened in a
          Modal or Side Panel renders HtmlReportWidgets directly (see DrillDownTarget),
          so a dialog does not get a copyright line across the bottom of it. */}
      {/* <AppVersionFooter /> */}
    </div>
  );
}
